import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

/* ---------- Types ---------- */
export interface PricingCardProps {
  title: string;
  amount: string;

  description?: string;      // optional
  credits?: string;          // optional

  features: string[];

  buttonLabel?: string;
  onButtonClick?: () => void;
  buttonDisabled?: boolean;

  mostPopular?: boolean;     // optional
  recommended?: boolean;     // optional
}

/* ---------- Component ---------- */
const PricingCard: React.FC<PricingCardProps> = ({
  title,
  amount,
  description,
  credits,
  features,

  buttonLabel = "Get Started",
  onButtonClick,
  buttonDisabled = false,

  mostPopular = false,
  recommended = false,
}) => {
  const SelectedColor = recommended
    ? "#5f9b20"
    : mostPopular
      ? "var(--action-button-background-color)"
      : buttonLabel === "Active"
        ? "#1e8e3e"
        : buttonLabel === "Downgrade" && !buttonDisabled
          // ? "#848884"
          ? "#d1d5db"
          : buttonLabel === "Downgrade" && buttonDisabled
            ? "#d1d5db"
            : "#d8d8d8";

  const SelectedListColor = recommended
    ? "#5f9b20"
    : mostPopular
      ? "var(--action-button-background-color)"
      : buttonLabel === "Active"
        ? "#1e8e3e"
        : buttonLabel === "Downgrade" && !buttonDisabled
          // ? "#848884"
          ? "#d1d5db"
          : buttonLabel === "Downgrade" && buttonDisabled
            ? "#d1d5db"
            : "#a6a6a6ff";
  return (
    <Box
      sx={{
        position: "relative",
        border: `1px solid ${SelectedColor}`,
        borderRadius: "var(--medium-large-border-radius)",
        p: 3,
        width: "100%",
        height: "100%",
        backgroundColor: recommended ? "#FAFFF5" : "#FFFFFF", //#FAFFF5
      }}
    >
      {/* Most Popular Chip */}
      {mostPopular && (
        <Chip
          label="Most Popular"
          size="small"
          sx={{
            position: "absolute",
            top: -12,
            left: 24,
            fontWeight: 600,
            backgroundColor: "var(--action-button-background-color)",
            color: "#ffffff"
          }}
        />
      )}

      {/* Title + Recommended */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>

        {recommended && (
          <Chip
            label="Recommended"
            size="small"
            sx={{
              backgroundColor: "#E7F5D9",
              color: "#4CAF50",
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      {/* Amount */}
      <Typography variant="h4" fontWeight={700} sx={{ mt: 2 }}>
        {amount}
      </Typography>

      {/* Credits (optional) */}
      {credits && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {credits}
        </Typography>
      )}

      {/* Description (optional) */}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {description}
        </Typography>
      )}

      {/* Action Button */}
      <Button
        fullWidth
        sx={{
          mt: 3,
          backgroundColor: buttonLabel === "Active"
            ? "rgba(30, 142, 62, 0.1)"
            : buttonLabel === "Downgrade" && !buttonDisabled
              // ? "#848884"
              ? "#f3f4f6"
              : buttonLabel === "Downgrade" && buttonDisabled
                ? "#d1d5db"
                : 'var(--action-button-background-color, #1a73e8) !important',
          color: buttonLabel === "Active" ? "#1e8e3e" : (buttonLabel === "Downgrade" ? "#000000" : '#fff'),
          border: 'none',
          cursor: 'pointer',
          padding: 'var(--action-button-small-padding)',
          borderRadius: 'var(--action-button-radius)',
          fontFamily: 'var(--font-family-manrope)',
          fontSize: 'var(--buttons-font-size, 16px)',
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',
          fontWeight: '400 !important',
          "&:hover": {
            boxShadow: "none"
          },
          "&.Mui-disabled": {
            backgroundColor: buttonLabel === "Active"
              ? "rgba(30, 142, 62, 0.1)"
              : buttonLabel === "Downgrade"
                ? "#d1d5db"
                : "primary.main",
            opacity: buttonLabel === "Active" ? 1 : buttonLabel === "Downgrade" ? 0.6 : 0.5,
            color: buttonLabel === "Active" ? "#1e8e3e" : "#ffffff"
          }
        }}
        onClick={onButtonClick}
        disabled={buttonDisabled}
      >
        {buttonLabel}
      </Button>

      {/* Features */}
      <Box sx={{ mt: 3 }}>
        {features?.map((feature: string, index: number) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <CheckIcon sx={{ color: SelectedListColor, fontSize: 18 }} />
            <Typography variant="body2">{feature}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PricingCard;
